import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useChat } from '../hooks/useChat';
import { useWeightLog } from '../hooks/useWeightLog';
import { useSubscription } from '../hooks/useSubscription';
import PlannerTab from './PlannerTab';
import DocumentsTab from './DocumentsTab';
import ContentLibraryTab from './ContentLibraryTab';
import { openFaqChannel } from '../lib/faqChannel';
import { getProteinTarget } from '../lib/nutrition';
import BetaCheckin from './BetaCheckin';
import Paywall from './Paywall';
import WeightChart from '../components/WeightChart';
import { LanguageToggle } from './LandingChat';
import { C } from '../lib/theme';

const CLINIC = {
  name: 'Clínica Sanart Alicante',
  phone: '+34626151624',
  address: 'C/Catedrático Ferre Vidiella, 9, Alicante, España',
  bookingUrl: 'https://clinicaalicantesanart.es/#reservar-cita',
};

function TabHome({ profile, userId, onSignOut, onNavigateToPlanner }) {
  const { t } = useTranslation();
  const { logs, addLog, kgLost, pctLost } = useWeightLog(userId);
  const [weightInput, setWeightInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [clinicOpen, setClinicOpen] = useState(false);

  const weeksOnTreatment = profile?.consent_at
    ? Math.floor((Date.now() - new Date(profile.consent_at)) / (1000 * 60 * 60 * 24 * 7))
    : 0;

  async function handleLogWeight(e) {
    e.preventDefault();
    if (!weightInput) return;
    setSaving(true);
    await addLog(weightInput);
    setWeightInput('');
    setSaving(false);
  }

  return (
    <div>
      <div style={{ background: `linear-gradient(135deg, ${C.sage}, ${C.sageLt})`, borderRadius: 16, padding: '1.5rem', color: '#fff', marginBottom: '1rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <h2 style={{ margin: '0 0 0.3rem', fontFamily: 'Georgia, serif', fontSize: '1.4rem' }}>
            {t('dashboard.greeting', { name: profile?.name || t('dashboard.default_name') })}
          </h2>
          <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
            <LanguageToggle light />
            <button onClick={onSignOut} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: 8, color: '#fff', fontSize: '0.75rem', padding: '0.3rem 0.7rem', cursor: 'pointer' }}>
              {t('common.sign_out')}
            </button>
          </div>
        </div>
        <p style={{ margin: '0 0 1.2rem', fontSize: '0.9rem', opacity: 0.85 }}>{t('dashboard.weekly_progress')}</p>
        <div style={{ display: 'flex', gap: '1rem' }}>
          {[
            { label: t('dashboard.kg_lost'),  value: kgLost > 0 ? `-${kgLost}` : '0' },
            { label: t('dashboard.pct_lost'), value: `${pctLost}%` },
            { label: t('dashboard.weeks'),    value: weeksOnTreatment },
          ].map(stat => (
            <div key={stat.label} style={{ flex: 1, background: 'rgba(255,255,255,0.15)', borderRadius: 10, padding: '0.6rem', textAlign: 'center' }}>
              <div style={{ fontFamily: 'Georgia, serif', fontSize: '1.4rem', fontWeight: 700 }}>{stat.value}</div>
              <div style={{ fontSize: '0.75rem', opacity: 0.8 }}>{stat.label}</div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ background: '#fff', borderRadius: 16, padding: '1.2rem', boxShadow: '0 2px 12px rgba(44,106,79,0.08)', marginBottom: '1rem' }}>
        <h3 style={{ margin: '0 0 0.8rem', color: C.carbon, fontFamily: 'Georgia, serif' }}>{t('dashboard.weight_evolution')}</h3>
        <WeightChart logs={logs} targetDate={profile?.target_date} goalWeight={profile?.weight_goal} />
        <form onSubmit={handleLogWeight} style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
          <input
            value={weightInput}
            onChange={e => setWeightInput(e.target.value)}
            type="number"
            step="0.1"
            placeholder={t('dashboard.log_weight_placeholder')}
            style={{ flex: 1, padding: '0.6rem 0.8rem', borderRadius: 8, border: `1.5px solid ${C.mint}`, background: C.cream, outline: 'none', fontSize: '0.95rem' }}
          />
          <button type="submit" disabled={saving} style={{ padding: '0.6rem 1.2rem', borderRadius: 8, border: 'none', background: C.sage, color: '#fff', fontWeight: 700, cursor: 'pointer' }}>
            {saving ? '...' : t('dashboard.save_btn')}
          </button>
        </form>
      </div>

      <div
        onClick={() => onNavigateToPlanner()}
        style={{ background: '#fff', borderRadius: 16, padding: '1.2rem', boxShadow: '0 2px 12px rgba(44,106,79,0.08)', marginBottom: '1rem', cursor: 'pointer', border: `2px solid ${C.mintLt}` }}
      >
        <h3 style={{ margin: '0 0 0.3rem', color: C.carbon, fontFamily: 'Georgia, serif' }}>{t('dashboard.weekly_planner')}</h3>
        <p style={{ margin: '0 0 0.8rem', color: C.gray, fontSize: '0.85rem' }}>{t('dashboard.planner_subtitle')}</p>
        <button style={{ padding: '0.6rem 1.2rem', borderRadius: 8, border: 'none', background: C.sage, color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: '0.9rem' }}>
          {t('dashboard.see_menu')}
        </button>
      </div>

      <div style={{ background: '#fff', borderRadius: 16, padding: '1.2rem', boxShadow: '0 2px 12px rgba(44,106,79,0.08)' }}>
        <h3 style={{ margin: '0 0 0.4rem', color: C.carbon, fontFamily: 'Georgia, serif' }}>{t('dashboard.partner_clinic')}</h3>
        <p style={{ margin: '0 0 0.8rem', color: C.gray, fontSize: '0.85rem' }}>{t('dashboard.clinic_subtitle')}</p>
        <button
          onClick={() => setClinicOpen(o => !o)}
          style={{ padding: '0.6rem 1.2rem', borderRadius: 8, border: `1.5px solid ${C.sage}`, background: clinicOpen ? C.sage : '#fff', color: clinicOpen ? '#fff' : C.sage, fontWeight: 600, cursor: 'pointer', fontSize: '0.9rem', width: '100%' }}
        >
          {clinicOpen ? t('dashboard.clinic_close') : t('dashboard.clinic_open')}
        </button>

        {clinicOpen && (
          <div style={{ marginTop: '1rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <a href={`tel:${CLINIC.phone}`} style={{ textDecoration: 'none' }}>
              <div style={{ background: C.mintLt, borderRadius: 12, padding: '0.9rem 1rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <span style={{ fontSize: '1.4rem' }}>📞</span>
                <div>
                  <div style={{ fontWeight: 700, color: C.carbon, fontSize: '0.95rem' }}>{CLINIC.phone}</div>
                  <div style={{ fontSize: '0.78rem', color: C.gray, marginTop: 2 }}>{CLINIC.address}</div>
                </div>
              </div>
            </a>

            <a href={CLINIC.bookingUrl} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
              <div style={{ background: C.sage, borderRadius: 12, padding: '0.9rem 1rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <span style={{ fontSize: '1.4rem' }}>📅</span>
                <div>
                  <div style={{ fontWeight: 700, color: '#fff', fontSize: '0.95rem' }}>{t('dashboard.book_online')}</div>
                  <div style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.8)', marginTop: 2 }}>{t('dashboard.book_online_sub')}</div>
                </div>
              </div>
            </a>

            <a href={CLINIC.bookingUrl} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
              <div style={{ border: `2px solid ${C.sage}`, borderRadius: 12, padding: '0.9rem 1rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <span style={{ fontSize: '1.4rem' }}>🎥</span>
                <div>
                  <div style={{ fontWeight: 700, color: C.sage, fontSize: '0.95rem' }}>{t('dashboard.book_video')}</div>
                  <div style={{ fontSize: '0.78rem', color: C.gray, marginTop: 2 }}>{t('dashboard.book_video_sub')}</div>
                </div>
              </div>
            </a>
          </div>
        )}
      </div>
    </div>
  );
}

function TabChat({ userId, canChat, messagesLeft, totalDaily, isTrial, onMessageSent, onRepeatedMedicalQuestion }) {
  const { t } = useTranslation();
  const { messages, typing, sendMessage } = useChat(userId, { onRepeatedMedicalQuestion });
  const [input, setInput] = useState('');
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, typing]);

  async function handleSend() {
    if (!input.trim() || !canChat) return;
    const text = input.trim();
    setInput('');
    await sendMessage(text);
    if (onMessageSent) onMessageSent();
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 130px)' }}>
      <div style={{ flex: 1, overflowY: 'auto', padding: '0.5rem', paddingBottom: '1rem' }}>
        {messages.length === 0 && (
          <div style={{ textAlign: 'center', padding: '2rem', color: C.gray }}>
            <img src="/logo192.png" style={{ width: '2.5rem', height: '2.5rem', objectFit: 'contain', marginBottom: '0.5rem' }} alt="" />
            <p style={{ fontSize: '0.95rem' }}>{t('dashboard.vera_intro')}</p>
          </div>
        )}
        {messages.map((msg, i) => (
          <div key={msg.id || i} style={{
            display: 'flex',
            justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
            marginBottom: '0.8rem',
            alignItems: 'flex-end',
            gap: '0.4rem',
          }}>
            {msg.role === 'assistant' && <img src="/logo192.png" style={{ width: '1.3rem', height: '1.3rem', objectFit: 'contain', flexShrink: 0 }} alt="" />}
            <div style={{
              maxWidth: '75%',
              padding: '0.75rem 1rem',
              borderRadius: msg.role === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
              background: msg.role === 'user' ? C.sage : '#fff',
              color: msg.role === 'user' ? '#fff' : C.carbon,
              fontSize: '0.9rem',
              lineHeight: 1.55,
              boxShadow: '0 1px 6px rgba(0,0,0,0.07)',
            }}>
              {msg.content}
            </div>
          </div>
        ))}
        {typing && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.8rem' }}>
            <img src="/logo192.png" style={{ width: '1.3rem', height: '1.3rem', objectFit: 'contain', flexShrink: 0 }} alt="" />
            <div style={{ background: '#fff', borderRadius: '16px 16px 16px 4px', padding: '0.75rem 1rem', boxShadow: '0 1px 6px rgba(0,0,0,0.07)', color: C.gray, fontSize: '0.88rem', fontStyle: 'italic' }}>
              {t('dashboard.vera_typing')}
            </div>
          </div>
        )}
        <div ref={endRef} />
      </div>

      <div style={{ borderTop: '1px solid #E5E7EB', padding: '0.75rem', background: C.cream }}>
        {isTrial && (
          <p style={{ margin: '0 0 0.4rem', fontSize: '0.72rem', color: canChat ? C.gray : C.terra, textAlign: 'center', fontWeight: canChat ? 400 : 700 }}>
            {canChat
              ? t('dashboard.trial_messages_left', { left: messagesLeft, total: totalDaily })
              : t('dashboard.trial_limit_reached')}
          </p>
        )}
        {!isTrial && (
          <p style={{ margin: '0 0 0.4rem', fontSize: '0.72rem', color: C.gray, textAlign: 'center' }}>
            {t('dashboard.disclaimer')}
          </p>
        )}
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <input
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleSend()}
            placeholder={canChat ? t('dashboard.chat_placeholder') : t('dashboard.chat_placeholder_limit')}
            disabled={!canChat}
            style={{
              flex: 1, padding: '0.7rem 1rem', borderRadius: 24,
              border: `1.5px solid ${canChat ? C.mint : '#E5E7EB'}`,
              background: canChat ? '#fff' : '#F3F4F6', outline: 'none', fontSize: '0.95rem',
              color: canChat ? C.carbon : C.gray,
            }}
          />
          <button
            onClick={handleSend}
            disabled={!canChat}
            style={{
              width: 44, height: 44, borderRadius: '50%', border: 'none',
              background: canChat ? C.sage : '#D1D5DB',
              color: '#fff', fontSize: '1.2rem',
              cursor: canChat ? 'pointer' : 'default',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            →
          </button>
        </div>
      </div>
    </div>
  );
}

function TabNutrition({ profile, onNavigateToPlanner }) {
  const { t } = useTranslation();
  const calories = profile ? Math.round((profile.weight_current || 80) * 24 * 0.85) : 1800;
  const protein = profile ? getProteinTarget(profile) : 130;

  return (
    <div>
      <div style={{ background: '#fff', borderRadius: 16, padding: '1.2rem', boxShadow: '0 2px 12px rgba(44,106,79,0.08)', marginBottom: '1rem' }}>
        <h3 style={{ margin: '0 0 1rem', color: C.carbon, fontFamily: 'Georgia, serif' }}>{t('dashboard.nutrition_title')}</h3>
        {[
          { label: t('dashboard.calories_label'), val: `~${calories} kcal`, pct: 65 },
          { label: t('dashboard.protein_label'),  val: `~${protein}g`,      pct: 72 },
          { label: t('dashboard.water_label'),    val: t('dashboard.water_value'), pct: 80 },
        ].map(o => (
          <div key={o.label} style={{ marginBottom: '0.8rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.88rem', marginBottom: 4 }}>
              <span style={{ color: C.carbon, fontWeight: 600 }}>{o.label}</span>
              <span style={{ color: C.sage, fontWeight: 700 }}>{o.val}</span>
            </div>
            <div style={{ height: 6, background: C.mintLt, borderRadius: 4 }}>
              <div style={{ width: `${o.pct}%`, height: '100%', background: C.sage, borderRadius: 4 }} />
            </div>
          </div>
        ))}
      </div>

      <div
        onClick={onNavigateToPlanner}
        style={{ background: '#fff', borderRadius: 16, padding: '1.2rem', boxShadow: '0 2px 12px rgba(44,106,79,0.08)', marginBottom: '1rem', cursor: 'pointer', border: `2px solid ${C.mintLt}` }}
      >
        <h3 style={{ margin: '0 0 0.3rem', color: C.carbon, fontFamily: 'Georgia, serif' }}>{t('dashboard.weekly_planner')}</h3>
        <p style={{ margin: '0 0 0.6rem', color: C.gray, fontSize: '0.85rem' }}>{t('dashboard.planner_diet_sub', { diet: profile?.diet || '' })}</p>
        <span style={{ fontSize: '0.85rem', color: C.sage, fontWeight: 700 }}>{t('dashboard.see_menu')}</span>
      </div>
    </div>
  );
}

function TabExercise() {
  const { t } = useTranslation();

  const EXERCISES = [
    { name: t('dashboard.ex_1_name'), sets: 3, reps: 12, tip: t('dashboard.ex_1_tip') },
    { name: t('dashboard.ex_2_name'), sets: 3, reps: 10, tip: t('dashboard.ex_2_tip') },
    { name: t('dashboard.ex_3_name'), sets: 3, reps: 12, tip: t('dashboard.ex_3_tip') },
    { name: t('dashboard.ex_4_name'), sets: 3, reps: 12, tip: t('dashboard.ex_4_tip') },
    { name: t('dashboard.ex_5_name'), sets: 3, reps: '30s', tip: t('dashboard.ex_5_tip') },
  ];

  return (
    <div>
      <div style={{ background: `linear-gradient(135deg, ${C.sage}, ${C.sageLt})`, borderRadius: 16, padding: '1.2rem', color: '#fff', marginBottom: '1rem' }}>
        <h3 style={{ margin: '0 0 0.3rem', fontFamily: 'Georgia, serif' }}>{t('dashboard.exercise_title')}</h3>
        <p style={{ margin: 0, fontSize: '0.85rem', opacity: 0.85 }}>{t('dashboard.exercise_subtitle')}</p>
      </div>
      {EXERCISES.map((ex, i) => (
        <div key={ex.name} style={{ background: '#fff', borderRadius: 16, padding: '1rem 1.2rem', boxShadow: '0 2px 12px rgba(44,106,79,0.08)', marginBottom: '0.8rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem' }}>
            <div style={{ width: 32, height: 32, borderRadius: '50%', background: C.mintLt, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, color: C.sage, fontSize: '0.9rem', flexShrink: 0 }}>
              {i + 1}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, color: C.carbon }}>{ex.name}</div>
              <div style={{ color: C.sage, fontSize: '0.85rem', fontWeight: 600 }}>{ex.sets} × {ex.reps}</div>
            </div>
          </div>
          <p style={{ margin: '0.5rem 0 0 2.5rem', fontSize: '0.82rem', color: C.gray, fontStyle: 'italic' }}>
            💡 {ex.tip}
          </p>
        </div>
      ))}
    </div>
  );
}

export default function UserDashboard({ user, profile, onSignOut, onProfileUpdate }) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('home');
  const [checkinSaving, setCheckinSaving] = useState(false);
  const [contentKind, setContentKind] = useState('sport_video');

  const {
    accessLevel, messagesLeft, canChat,
    recordMessage, submitCheckin, TRIAL_DAILY_MESSAGES,
  } = useSubscription(user?.id, user, profile, onProfileUpdate);

  const TABS = [
    { id: 'home',      label: t('dashboard.tab_home'),      icon: '🏠' },
    { id: 'chat',      label: t('dashboard.tab_chat'),      icon: <img src="/logo192.png" style={{ width: '1.4rem', height: '1.4rem', objectFit: 'contain' }} alt="Vera" /> },
    { id: 'nutrition', label: t('dashboard.tab_nutrition'), icon: '🥗' },
    { id: 'exercise',  label: t('dashboard.tab_exercise'),  icon: '💪' },
    { id: 'planner',   label: t('dashboard.tab_planner'),   icon: '📅' },
    { id: 'content',   label: t('dashboard.tab_content'),   icon: '🎬' },
    { id: 'documents', label: t('dashboard.tab_docs'),      icon: '📋' },
  ];

  return (
    <div style={{ maxWidth: 480, margin: '0 auto', fontFamily: 'sans-serif', background: C.cream, minHeight: '100vh', position: 'relative', paddingBottom: 70 }}>
      <div style={{ padding: '1rem', paddingBottom: activeTab === 'chat' ? 0 : '1rem' }}>
        {activeTab === 'home' && (
          <TabHome profile={profile} userId={user.id} onSignOut={onSignOut} onNavigateToPlanner={() => setActiveTab('planner')} />
        )}

        {activeTab === 'chat' && (() => {
          if (accessLevel === 'beta_needs_checkin') {
            return (
              <BetaCheckin
                saving={checkinSaving}
                onSubmit={async data => {
                  setCheckinSaving(true);
                  await submitCheckin(data);
                  setCheckinSaving(false);
                }}
              />
            );
          }
          if (accessLevel === 'expired') {
            return <Paywall trialDaysLeft={0} userId={user?.id} />;
          }
          return (
            <TabChat
              userId={user.id}
              canChat={canChat}
              messagesLeft={messagesLeft}
              totalDaily={TRIAL_DAILY_MESSAGES}
              isTrial={accessLevel === 'trial'}
              onMessageSent={recordMessage}
              onRepeatedMedicalQuestion={openFaqChannel}
            />
          );
        })()}

        {activeTab === 'nutrition' && (
          <TabNutrition profile={profile} onNavigateToPlanner={() => setActiveTab('planner')} />
        )}

        {activeTab === 'exercise' && <TabExercise />}

        {activeTab === 'planner' && (() => {
          if (accessLevel === 'beta_needs_checkin') {
            return (
              <BetaCheckin
                saving={checkinSaving}
                onSubmit={async data => {
                  setCheckinSaving(true);
                  await submitCheckin(data);
                  setCheckinSaving(false);
                }}
              />
            );
          }
          if (accessLevel === 'expired') {
            return <Paywall trialDaysLeft={0} userId={user?.id} />;
          }
          return <PlannerTab userId={user.id} profile={profile} onProfileUpdate={onProfileUpdate} />;
        })()}

        {activeTab === 'content' && (
          <ContentLibraryTab userId={user.id} profile={profile} kind={contentKind} onKindChange={setContentKind} />
        )}

        {activeTab === 'documents' && (
          <DocumentsTab userId={user.id} profile={profile} />
        )}
      </div>

      <nav style={{
        position: 'fixed', bottom: 0, left: '50%', transform: 'translateX(-50%)',
        width: '100%', maxWidth: 480,
        background: '#fff', borderTop: '1px solid #E5E7EB',
        display: 'flex', zIndex: 100,
      }}>
        {TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              flex: 1, padding: '0.6rem 0', border: 'none', background: 'none', cursor: 'pointer',
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.1rem',
              color: activeTab === tab.id ? C.sage : C.gray,
              fontSize: '1.2rem',
            }}
          >
            <span>{tab.icon}</span>
            <span style={{ fontSize: '0.65rem', fontWeight: activeTab === tab.id ? 700 : 400 }}>{tab.label}</span>
          </button>
        ))}
      </nav>
    </div>
  );
}
